import React from 'react';
import { FormattedMessage } from 'react-intl';
import { SelectedTrips } from '../../BookingWizardWrap';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { TripSelectionCalendar } from './TripSelectionCalendar';
import TripOption from './TripOption/TripOption';
import SelectedFareTrip from './SelectedFareTrip';
import TripLegSummary from './TripLegSummary';
import { appConfigStore } from 'Models/AppConfigStore';
import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import ErrorMessage from 'Views/Components/New/ErrorMessage';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useClearStaleTicket } from './Hooks/useClearStaleTicket';

interface ReturnTripSectionProps {
	wizardData: BookingWizardData;
	startLocation: { name: string } | undefined;
	endLocation: { name: string } | undefined;
	selectedReturningTrip: TicketsTabTrip | undefined;
	trips: TicketsTabTrip[] | null;
	selectedTrips: SelectedTrips;
	isStaff: boolean;
	priceByFerryTrip: { [key: string]: number };
	classPricesByFerryTrip: { [key: string]: TripClassPrice[] };
	/**
	 * Whether the class fares are still being fetched.
	 *
	 * <p>Nothing is drawn until they land. Without it the step reads an empty map as "this vessel has
	 * no classes", draws the plain card, and redraws as a fare table the moment the fares arrive —
	 * which is a flicker on every window and, briefly, the wrong screen.</p>
	 */
	classPricesLoading?: boolean;
	pricesLoading: boolean;
	tripsLoading: boolean;
	/**
	 * Fetches this leg's sailings again. Passed to the cards so joining a waitlist redraws the
	 * sailing with its waitlisted bell — without it the join modal can only say `Refresh the page`.
	 */
	refreshTrips?: () => void;
	currentTab: number;
	setCurrentTab: (date: number) => void;
	onRemove: () => void;
}

export function ReturnTripSection({
	wizardData,
	startLocation,
	endLocation,
	selectedReturningTrip,
	trips,
	selectedTrips,
	isStaff,
	priceByFerryTrip,
	classPricesByFerryTrip,
	classPricesLoading,
	pricesLoading,
	tripsLoading,
	refreshTrips,
	currentTab,
	setCurrentTab,
	onRemove,
}: ReturnTripSectionProps) {
	// On-screen error so ScrollToError() can scroll to the unselected returning trip.
	const setWizardData = useBookingWizardDataStore(x => x.setWizardData);

	/**
	 * Whether this sailing is sold by class.
	 *
	 * <p>Classes on <b>and this vessel has some</b>. The setting decides whether the step is a fare
	 * table; the sailing's own fares decide whether it is a row of one. A sailing with none has no
	 * class to name, no upgrade to offer and no fare cell to click, and drawing it as a fare card
	 * gave the booker a card they could not select — as well as handing <c>SelectedFareTrip</c> an
	 * undefined column list to read a chosen class out of.</p>
	 *
	 * <p>It used to also require the classes to differ in price, which meant a vessel whose classes
	 * cost the same was sold as though it had none — the one place the booker chooses between them
	 * stopped offering the choice precisely because the choice was free.</p>
	 */
	const showFareColumns = (trip: TicketsTabTrip) =>
		appConfigStore.passengerClassesEnabled && (classPricesByFerryTrip[trip.id]?.length ?? 0) > 0;

	const baseWizardErrors = useBookingWizardDataStore(x => x.baseWizardErrors);
	const setBaseWizardErrors = useBookingWizardDataStore(x => x.setBaseWizardErrors);
	const showTripError =
		!!baseWizardErrors.returningTicketId && !selectedReturningTrip && !wizardData.returningTicketId;
	// A sailing this window cannot account for — the route or the direction has changed under it.
	useClearStaleTicket({
		ticketId: wizardData.returningTicketId,
		selectedTrip: selectedReturningTrip,
		trips,
		loading: tripsLoading || !!classPricesLoading,
		clear: () => {
			const next = new BookingWizardData({ ...wizardData });
			next.returningTicketId = undefined;
			next.returningJourney = undefined;
			setWizardData(next);
		},
	});

	const selectionClass = showTripError
		? 'ticket-tab__trip-selection ticket-tab__trip-selection--error'
		: 'ticket-tab__trip-selection';

	// Once a returning trip is selected, clear the error so it doesn't reappear on deselect (it
	// should only return when the user presses Continue again without a selection).
	React.useEffect(() => {
		if (wizardData.returningTicketId && baseWizardErrors.returningTicketId) {
			setBaseWizardErrors({ ...baseWizardErrors, returningTicketId: undefined });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wizardData.returningTicketId]);

	return (
		<>
			<div className="ticket-tab__sub-header return-booking inline-button-container ticket-tab-text">
				<h4>
					<FormattedMessage
						id="booking-wizard__tickets__returning-section-title"
						defaultMessage="Returning Trip"
					/>
				</h4>
				<If condition={wizardData.wizardMode !== 'ALTERATION'}>
					<Button
						className="edit-filter-button"
						colors={Colors.Red}
						display={Display.Text}
						onClick={onRemove}
					>
						Remove
					</Button>
				</If>
			</div>

			{/* Reversed: the returning leg runs the other way. */}
			<TripLegSummary
				from={endLocation?.name}
				to={startLocation?.name}
				date={wizardData.ticketSelectionEndDate}
				dateInRail={!selectedReturningTrip}
			/>

			{/*
				Nothing is drawn until we know whether this vessel sells by class. An empty fare map
				means "not yet" as readily as it means "no classes", and committing to the plain card
				on the strength of it is what made the list flicker from cards to a fare table.
			*/}
			{classPricesLoading && (
				<div className="spinner-container">
					<LottieSpinner className="relative" />
				</div>
			)}

			{/* Show calendar when no trip is selected — wrapped in an error box when the user tried
			    to continue without selecting a trip. */}
			{!classPricesLoading && !selectedReturningTrip && !wizardData.returningTicketId && (
				<div className={selectionClass} data-test-id="trip-list-return">
					<If condition={showTripError}>
						<ErrorMessage className="ticket-tab__trip-error" error={baseWizardErrors.returningTicketId} />
					</If>
					<TripSelectionCalendar
						ferryTrips={trips}
						departure={false}
						selectedTrips={selectedTrips}
						isStaff={isStaff}
						refreshTrips={refreshTrips}
						priceByFerryTrip={priceByFerryTrip}
						classPricesByFerryTrip={classPricesByFerryTrip}
						pricesLoading={pricesLoading}
						tripsLoading={tripsLoading}
						currentTab={currentTab}
						setCurrentTab={setCurrentTab}
					/>
				</div>
			)}

			{/* Show selected trip */}
			{!classPricesLoading && wizardData.returningTicketId && (
				<>
					{!selectedReturningTrip && (
						<div className="spinner-container">
							<LottieSpinner className="relative" />
						</div>
					)}
					{/*
						Once chosen, the sailing collapses: the two classes the booker passed over
						stop taking the width the one they took needs, and come back as a single
						upgrade line. A vessel without priced classes keeps the card it always had.
					*/}
					{selectedReturningTrip && showFareColumns(selectedReturningTrip) && (
						<SelectedFareTrip
							trip={selectedReturningTrip}
							classPrices={classPricesByFerryTrip[selectedReturningTrip.id]}
							wizardData={wizardData}
							saveChanges={setWizardData}
							legType="return"
							// Reversed, like the summary above: the returning leg runs the other way.
							from={endLocation?.name}
							to={startLocation?.name}
							alterationDelta={
								wizardData.wizardMode === 'ALTERATION'
									? priceByFerryTrip[selectedReturningTrip.id] ?? null
									: null
							}
						/>
					)}
					{selectedReturningTrip && !showFareColumns(selectedReturningTrip) && (
						<TripOption
							isLoading={pricesLoading}
							trip={selectedReturningTrip}
							isSelected
							price={priceByFerryTrip[selectedReturningTrip.id]}
							legType="return"
						/>
					)}
				</>
			)}
		</>
	);
}
